import * as Yup from 'yup';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  Box,
  Alert,
  Stack,
  Button,
  Dialog,
  Divider,
  MenuItem,
  useTheme,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent,
  useMediaQuery,
  CircularProgress
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import {
  CalendarEventType,
  CreateCalendarEventDto,
  UpdateCalendarEventDto,
  CalendarEventVisibility
} from 'src/services/calendar/calendar.types';
import {
  useGetCalendarEventByIdQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation
} from 'src/services/calendar/calendar.service';

import RHFSwitch from 'src/components/hook-form/rhf-switch';
import { RHFSelect } from 'src/components/hook-form/rhf-select';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFTextField from 'src/components/hook-form/rhf-text-field';

// ----------------------------------------------------------------------

interface CalendarEventEditorProps {
  calendarId: string;
  eventId?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type FormValues = {
  title: string;
  description?: string;
  start_at: moment.Moment;
  end_at: moment.Moment | null;
  all_day: boolean;
  location?: string;
  external_url?: string;
  type: CalendarEventType;
};

export default function CalendarEventEditor({
  calendarId,
  eventId,
  open,
  onClose,
  onSuccess
}: CalendarEventEditorProps) {

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isEditMode = !!eventId;
  const [error, setError] = useState<string | null>(null);

  const { account } = useAuthContext();

  const { data: eventData, isLoading: isLoadingEvent } = useGetCalendarEventByIdQuery(
    { eventId: eventId || '' },
    { skip: !isEditMode || !open }
  );

  const [createEvent, { isLoading: isCreating }] = useCreateCalendarEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateCalendarEventMutation();

  const isSubmitting = isCreating || isUpdating;

  const isPrivateEvent = useMemo(() => eventData?.visibility === CalendarEventVisibility.PRIVATE, [eventData]);

  const EventSchema = Yup.object().shape({
    title: Yup.string().required(t('calendar.editor.validation.title_required')),
    description: Yup.string(),
    start_at: Yup.mixed()
      .required(t('calendar.editor.validation.start_required'))
      .test('is-moment', 'Must be a valid date', value => moment.isMoment(value) && value.isValid()),
    end_at: Yup.mixed()
      .nullable()
      .when('all_day', {
        is: false,
        then: (schema) =>
          schema.test({
            name: 'end-after-start',
            message: t('calendar.editor.validation.end_after_start'),
            test(value) {
              // eslint-disable-next-line
              const { start_at } = this.parent;
              if (!value || !start_at) return true;
              return moment(value).isAfter(moment(start_at));
            }
          })
      }),
    all_day: Yup.boolean(),
    location: Yup.string(),
    external_url: Yup.string().url(t('validation.url')),
    type: Yup.string().required(t('calendar.editor.validation.type_required')),
  });

  const defaultValues: FormValues = useMemo(() => ({
    title: '',
    description: '',
    start_at: moment().tz(account.timezone),
    end_at: moment().tz(account.timezone).add(1, 'hour'),
    all_day: false,
    location: '',
    external_url: '',
    type: CalendarEventType.MEETING,
  }), [account.timezone]);

  const methods = useForm<FormValues>({
    resolver: yupResolver(EventSchema) as any,
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const all_day = watch('all_day');
  const start_at = watch('start_at');
  const end_at = watch('end_at');

  useEffect(() => {
    if (open) {
      // if start date changes, and there is no end date, set end date to start date + 1 hour
      if (start_at && !end_at) {
        setValue('end_at', moment(start_at).clone().add(1, 'hour'));
      }
      // if end date changes, and it is before start date, set end date to start date + 1 hour
      if (end_at && start_at && moment(end_at).isSameOrBefore(moment(start_at))) {
        setValue('end_at', moment(start_at).clone().add(1, 'hour'));
      }
      // if all_day is true, set start_at and end_at to start of the day
      if (all_day) {
        const start = moment(start_at).clone().tz(account.timezone).startOf('day');
        setValue('start_at', start);
        setValue('end_at', null); // clear end_at for all-day events
      }

      // if all_day is false, ensure end_at is set to start_at + 1 hour
      if (!all_day && start_at && !end_at) {
        setValue('end_at', moment(start_at).clone().add(1, 'hour'));
      }
    }
  }, [open, start_at, end_at, all_day, setValue, account.timezone]);

  useEffect(() => {
    if (open) {
      if (isEditMode && eventData) {
        // Format dates properly
        const start = moment(eventData.start_at).tz(account.timezone);
        const end = eventData.end_at ? moment(eventData.end_at).tz(account.timezone) : null;

        reset({
          title: eventData.title,
          description: eventData.description || '',
          start_at: start.isValid() ? start : moment().tz(account.timezone),
          end_at: end && end.isValid() ? end : null,
          all_day: eventData.all_day,
          location: eventData.location || '',
          type: eventData.type,
        });
      } else {
        reset(defaultValues);
      }
      setError(null);
    }
  }, [open, eventData, isEditMode, reset, defaultValues, account.timezone]);

  const onSubmit = async (data: FormValues) => {
    try {
      setError(null);

      if (isEditMode && eventId) {
        // Update existing event
        const updateData: UpdateCalendarEventDto = {
          title: data.title,
          description: data.description || undefined,
          start_at: data.start_at.toDate(),
          end_at: !data.all_day && data.end_at ? data.end_at.toDate() : undefined,
          all_day: data.all_day,
          external_url: data.external_url || undefined,
          location: data.location || undefined,
          type: data.type,
        };

        await updateEvent({
          eventId,
          data: updateData
        }).unwrap();
      } else {
        // Create new event
        const createData: CreateCalendarEventDto = {
          title: data.title,
          description: data.description || undefined,
          start_at: data.start_at.toDate(), // Convert moment to Date
          end_at: !data.all_day && data.end_at ? data.end_at.toDate() : undefined,
          all_day: data.all_day,
          external_url: data.external_url || undefined,
          location: data.location || undefined,
          type: data.type,
        };

        await createEvent({
          calendarId,
          data: createData
        }).unwrap();
      }

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || t('calendar.editor.error.submit'));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isEditMode
            ? t('calendar.editor.edit_event')
            : t('calendar.editor.new_event')}
          <IconButton
            edge="end"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="close"
          >
            <Box component="span" className="icon icon-close" sx={{ width: 20, height: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {isLoadingEvent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={account.timezone}>

            {
              eventData?.visibility === CalendarEventVisibility.PRIVATE && (
                <Box sx={{ pb: 2 }}>
                  <Alert severity="warning">{t('calendar.event_private_notice')}</Alert>
                </Box>
              )
            }

            <FormProvider methods={methods}>
              <Stack spacing={3}>
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <RHFTextField
                  name="title"
                  disabled={isPrivateEvent || isSubmitting}
                  label={t('calendar.editor.title')}
                  autoFocus
                />

                <RHFTextField
                  name="description"
                  disabled={isPrivateEvent || isSubmitting}
                  label={t('calendar.editor.description')}
                  multiline
                  rows={3}
                />

                <RHFTextField
                  name="location"
                  disabled={isPrivateEvent || isSubmitting}
                  label={t('calendar.editor.location')}
                />

                <RHFTextField
                  name="external_url"
                  disabled={isPrivateEvent || isSubmitting}
                  label={t('calendar.editor.external_url')}
                  type='url'
                />

                <RHFSelect
                  name="type"
                  label={t('calendar.editor.type')}
                  disabled={isPrivateEvent || isSubmitting}
                >
                  <MenuItem value={CalendarEventType.MEETING}>
                    {t('calendar.event_types.meeting')}
                  </MenuItem>
                  <MenuItem value={CalendarEventType.TASK}>
                    {t('calendar.event_types.task')}
                  </MenuItem>
                  <MenuItem value={CalendarEventType.REMINDER}>
                    {t('calendar.event_types.reminder')}
                  </MenuItem>
                  <MenuItem value={CalendarEventType.OTHER}>
                    {t('calendar.event_types.other')}
                  </MenuItem>
                </RHFSelect>

                <RHFSwitch
                  name="all_day"
                  disabled={isPrivateEvent || isSubmitting}
                  label={t('calendar.editor.all_day')}
                  onChange={(event, checked) => {
                    if (checked) {
                      // When switching to all-day, adjust the dates
                      const start = watch('start_at');
                      if (start) {
                        const allDayStart = moment(start).tz(account.timezone).startOf('day');
                        setValue('start_at', allDayStart);
                        setValue('end_at', null);
                      }
                    }
                  }}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Controller
                    name="start_at"
                    disabled={isPrivateEvent || isSubmitting}
                    control={control}
                    render={({ field: { value, onChange, ...field } }) => (
                      <Box sx={{ flex: 1 }}>
                        {all_day ? (
                          <DatePicker
                            {...field}
                            label={t('calendar.editor.start_date')}
                            value={value}
                            timezone={account.timezone}
                            onChange={(newValue) => {
                              onChange(newValue);
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.start_at,
                                helperText: errors.start_at?.message,
                              },
                            }}
                          />
                        ) : (
                          <DateTimePicker
                            {...field}
                            label={t('calendar.editor.start_datetime')}
                            value={value}
                            timezone={account.timezone}
                            onChange={(newValue) => {
                              onChange(newValue);
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.start_at,
                                helperText: errors.start_at?.message
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}
                  />

                  {!all_day && (
                    <Controller
                      name="end_at"
                      control={control}
                      disabled={isPrivateEvent || isSubmitting}
                      render={({ field: { value, onChange, ...field } }) => (
                        <Box sx={{ flex: 1 }}>
                          <DateTimePicker
                            {...field}
                            label={t('calendar.editor.end_datetime')}
                            value={value}
                            timezone={account.timezone}
                            onChange={(newValue) => {
                              onChange(newValue);
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.end_at,
                                helperText: errors.end_at?.message,
                              },
                            }}
                          />
                        </Box>
                      )}
                    />
                  )}
                </Box>
              </Stack>
            </FormProvider>
          </LocalizationProvider>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={isSubmitting || isLoadingEvent || isPrivateEvent}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          onClick={handleSubmit(onSubmit)}
        >
          {isEditMode ? t('common.update') : t('common.create')}
        </Button>
      </DialogActions>

    </Dialog>
  );
}